import React, { useState, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { CameraIcon, CheckCircleIcon, CloudIcon, Loader2Icon, PencilIcon, TrashIcon, XIcon } from 'lucide-react';
function UserPlaceholderIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props} data-id="element-96">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" data-id="element-97" />
      <circle cx="12" cy="7" r="4" data-id="element-98" />
    </svg>;
}
export function AccountSettings() {
  const {
    isArabic
  } = useLanguage();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: 'John Doe',
    email: 'john@example.com',
    language: 'beginner',
    bio: '',
    notifications: {
      email: true,
      push: true
    }
  });
  const [previewImage, setPreviewImage] = useState<string | null>('https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80');
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) return;
    const file = event.target.files[0];
    // Create a preview URL
    const reader = new FileReader();
    reader.onload = e => {
      if (e.target?.result) {
        setPreviewImage(e.target.result as string);
      }
    };
    reader.readAsDataURL(file);
    // Simulate upload process
    setIsUploading(true);
    setUploadProgress(0);
    setUploadSuccess(false);
    // Simulate progress
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);
          setUploadSuccess(true);
          setTimeout(() => setUploadSuccess(false), 3000);
          return 100;
        }
        return prev + 10;
      });
    }, 300);
  };
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  const removeImage = () => {
    setPreviewImage(null);
    setShowImageOptions(false);
  };
  return <div className="space-y-8" data-id="element-99">
      {/* Profile Picture */}
      <div data-id="element-100">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-101">
          {isArabic ? 'الصورة الشخصية' : 'Profile Picture'}
        </h3>
        <div className="flex items-center gap-6" data-id="element-102">
          <div className="relative group" data-id="element-103">
            <div className={`w-32 h-32 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 ${!previewImage ? 'flex items-center justify-center' : ''}`} data-id="element-104">
              {previewImage ? <img src={previewImage} alt="Profile" className="w-full h-full object-cover" data-id="element-105" /> : <UserPlaceholderIcon className="w-16 h-16 text-gray-400 dark:text-gray-500" data-id="element-106" />}
              {/* Upload progress overlay */}
              {isUploading && <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center" data-id="element-107">
                  <Loader2Icon className="w-8 h-8 text-white animate-spin mb-2" data-id="element-108" />
                  <div className="w-4/5 bg-gray-200 rounded-full h-1.5 mb-1" data-id="element-109">
                    <div className="bg-blue-600 h-1.5 rounded-full" style={{
                  width: `${uploadProgress}%`
                }} data-id="element-110"></div>
                  </div>
                  <div className="text-xs text-white" data-id="element-111">{uploadProgress}%</div>
                </div>}
              {/* Success overlay */}
              {uploadSuccess && <div className="absolute inset-0 bg-green-500/70 flex items-center justify-center" data-id="element-112">
                  <CheckCircleIcon className="w-12 h-12 text-white" data-id="element-113" />
                </div>}
            </div>
            {/* Hover overlay */}
            {!isUploading && !uploadSuccess && <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer" onClick={() => setShowImageOptions(true)} data-id="element-114">
                <PencilIcon className="w-6 h-6 text-white" data-id="element-115" />
              </div>}
            {/* Image options popover */}
            {showImageOptions && <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 z-10" data-id="element-116">
                <div className="flex flex-col" data-id="element-117">
                  <button onClick={triggerFileInput} className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md" data-id="element-118">
                    <CloudIcon className="w-4 h-4" data-id="element-119" />
                    <span data-id="element-120">{isArabic ? 'تحميل صورة' : 'Upload image'}</span>
                  </button>
                  {previewImage && <button onClick={removeImage} className="flex items-center gap-2 px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md" data-id="element-121">
                      <TrashIcon className="w-4 h-4" data-id="element-122" />
                      <span data-id="element-123">{isArabic ? 'إزالة الصورة' : 'Remove image'}</span>
                    </button>}
                  <button onClick={() => setShowImageOptions(false)} className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md" data-id="element-124">
                    <XIcon className="w-4 h-4" data-id="element-125" />
                    <span data-id="element-126">{isArabic ? 'إلغاء' : 'Cancel'}</span>
                  </button>
                </div>
              </div>}
          </div>
          <div data-id="element-127">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1" data-id="element-128">
              {isArabic ? 'الحجم الموصى به: 400x400 بكسل' : 'Recommended size: 400x400 pixels'}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-129">
              {isArabic ? 'PNG، JPG أو GIF حتى 2MB' : 'PNG, JPG or GIF up to 2MB'}
            </p>
            <button onClick={triggerFileInput} className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors text-sm" data-id="element-130">
              <CameraIcon className="w-4 h-4" data-id="element-131" />
              <span data-id="element-132">{isArabic ? 'تغيير الصورة' : 'Change photo'}</span>
            </button>
            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} data-id="element-133" />
          </div>
        </div>
      </div>
      {/* Personal Information */}
      <div data-id="element-134">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-135">
          {isArabic ? 'المعلومات الشخصية' : 'Personal Information'}
        </h3>
        <div className="space-y-4" data-id="element-136">
          <div data-id="element-137">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-138">
              {isArabic ? 'الاسم' : 'Name'}
            </label>
            <input type="text" value={formData.name} onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-139" />
          </div>
          <div data-id="element-140">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-141">
              {isArabic ? 'البريد الإلكتروني' : 'Email'}
            </label>
            <input type="email" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-142" />
          </div>
          <div data-id="element-143">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-144">
              {isArabic ? 'المستوى' : 'Level'}
            </label>
            <select value={formData.language} onChange={e => setFormData({
            ...formData,
            language: e.target.value
          })} className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-145">
              <option value="beginner" data-id="element-146">
                {isArabic ? 'مبتدئ' : 'Beginner'}
              </option>
              <option value="intermediate" data-id="element-147">
                {isArabic ? 'متوسط' : 'Intermediate'}
              </option>
              <option value="advanced" data-id="element-148">
                {isArabic ? 'متقدم' : 'Advanced'}
              </option>
            </select>
          </div>
          <div data-id="element-149">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-150">
              {isArabic ? 'نبذة' : 'Bio'}
            </label>
            <textarea value={formData.bio} onChange={e => setFormData({
            ...formData,
            bio: e.target.value
          })} rows={4} className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder={isArabic ? 'اكتب نبذة عنك...' : 'Write something about yourself...'} data-id="element-151" />
          </div>
        </div>
      </div>
      {/* Email Preferences */}
      <div data-id="element-152">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-153">
          {isArabic ? 'تفضيلات البريد الإلكتروني' : 'Email Preferences'}
        </h3>
        <div className="space-y-4" data-id="element-154">
          <label className="flex items-center gap-3" data-id="element-155">
            <input type="checkbox" checked={formData.notifications.email} onChange={e => setFormData({
            ...formData,
            notifications: {
              ...formData.notifications,
              email: e.target.checked
            }
          })} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-156" />
            <span className="text-gray-700 dark:text-gray-300" data-id="element-157">
              {isArabic ? 'تلقي إشعارات البريد الإلكتروني' : 'Receive email notifications'}
            </span>
          </label>
          <label className="flex items-center gap-3" data-id="element-158">
            <input type="checkbox" checked={formData.notifications.push} onChange={e => setFormData({
            ...formData,
            notifications: {
              ...formData.notifications,
              push: e.target.checked
            }
          })} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-159" />
            <span className="text-gray-700 dark:text-gray-300" data-id="element-160">
              {isArabic ? 'تلقي الإشعارات المباشرة' : 'Receive push notifications'}
            </span>
          </label>
        </div>
      </div>
    </div>;
}